/*
 * Live builds: pure model. How an SSE `build.*` event patches a cached build, and which status transitions make the
 * checks and artifacts worth refetching. No React and no query client here; `useBuildsLive` applies the result.
 */
import { BuildStatus } from '@hangar/shared';
import { Build, type Build as BuildT } from '@/api/schemas';
import type { LiveEvent } from '@/features/live/liveModel';

export type Page = { data: BuildT[]; nextCursor: string | null };
export type BuildPatch = (b: BuildT) => BuildT;

/** The build an event is about (`subject.type === 'build'`), if any. */
export const eventBuildId = (ev: LiveEvent): string | undefined => (ev.subject.type === 'build' ? ev.subject.id : undefined);

/** Statuses after which the build has new checks and artifacts to show. */
const REFRESH_DETAILS = new Set<BuildStatus>(['verifying', 'succeeded', 'failed']);

export const refreshesDetails = (status: BuildStatus | undefined): boolean => status !== undefined && REFRESH_DETAILS.has(status);

/** The status an event moves a build to (`toStatus`, else `status`), when it is a known one. */
export function eventStatus(ev: LiveEvent): BuildStatus | undefined {
  const parsed = BuildStatus.safeParse(ev.data.toStatus ?? ev.data.status);
  return parsed.success ? parsed.data : undefined;
}

/**
 * Patch for one cached build: a full build in the event replaces the cached one; otherwise only the status and
 * `updatedAt` of the build the event is about move. Any other build is returned untouched (same reference).
 */
export function patchFromEvent(ev: LiveEvent, buildId: string | undefined): BuildPatch {
  const full = Build.safeParse(ev.data.build);
  const status = eventStatus(ev);
  return (b) => {
    if (full.success && full.data.id === b.id) return full.data;
    if (b.id !== buildId) return b;
    return { ...b, status: status ?? b.status, updatedAt: ev.occurredAt };
  };
}

/** Applies a patch to every page of a cached list; `touched` tells whether any build changed (else `data` is the input). */
export function patchPages(old: { pages: Page[] }, patch: BuildPatch): { pages: Page[]; touched: boolean } {
  let touched = false;
  const pages = old.pages.map((p) => ({
    ...p,
    data: p.data.map((b) => {
      const next = patch(b);
      if (next !== b) touched = true;
      return next;
    }),
  }));
  return { pages, touched };
}

/** Whether the lists must be refetched: the event matched nothing in the cache, or a new build was created. */
export const needsListRefetch = (found: boolean, ev: LiveEvent): boolean => !found || ev.type === 'build.created';
